/**
 * Copyright (c) 2018-2026 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author David Sehnal <david.sehnal@gmail.com>
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 * @author Yakov Pechersky <ffxen158@gmail.com>
 */

import { CIF } from '../../mol-io/reader/cif';
import { Mat4, Vec3 } from '../../mol-math/linear-algebra';
import { volumeFromCcp4 } from '../../mol-model-formats/volume/ccp4';
import { volumeFromDensityServerData } from '../../mol-model-formats/volume/density-server';
import { volumeFromDsn6 } from '../../mol-model-formats/volume/dsn6';
import { RuntimeContext, Task } from '../../mol-task';
import { ParamDefinition as PD } from '../../mol-util/param-definition';
import { PluginStateObject as SO, PluginStateTransform } from '../objects';
import { volumeFromCube } from '../../mol-model-formats/volume/cube';
import { volumeFromDx } from '../../mol-model-formats/volume/dx';
import { Grid, Volume } from '../../mol-model/volume';
import { PluginContext } from '../../mol-plugin/context';
import { StateSelection, StateTransform, StateTransformer } from '../../mol-state';
import { volumeFromSegmentationData } from '../../mol-model-formats/volume/segmentation';
import { volumeFromStructureFactors, StructureFactorMapType } from '../../mol-model-formats/volume/structure-factors';
import { volumeFromMtz } from '../../mol-model-formats/volume/mtz';
import { getTransformFromParams, TransformParam, transformParamsNeedCentroid } from './helpers';

export { VolumeFromCcp4 };
export { VolumeFromDsn6 };
export { VolumeFromCube };
export { VolumeFromDx };
export { AssignColorVolume };
export { VolumeFromDensityServerCif };
export { VolumeFromSegmentationCif };
export { VolumeFromStructureFactorsCif };
export { VolumeFromMtz };
export { VolumeTransform };
export { VolumeInstances };
export { CustomVolumeProperties };

type VolumeFromCcp4 = typeof VolumeFromCcp4
const VolumeFromCcp4 = PluginStateTransform.BuiltIn({
    name: 'volume-from-ccp4',
    display: { name: 'Volume from CCP4/MRC/MAP', description: 'Create Volume from CCP4/MRC/MAP data' },
    from: SO.Format.Ccp4,
    to: SO.Volume.Data,
    params(a) {
        return {
            voxelSize: PD.Vec3(Vec3.create(1, 1, 1)),
            offset: PD.Vec3(Vec3.create(0, 0, 0)),
            entryId: PD.Text(''),
        };
    }
})({
    apply({ a, params }) {
        return Task.create('Create volume from CCP4/MRC/MAP', async ctx => {
            const volume = await volumeFromCcp4(a.data, { ...params, label: a.data.name || a.label }).runInContext(ctx);
            const props = { label: volume.label || 'Volume', description: `Volume ${a.data.header.NX}\u00D7${a.data.header.NX}\u00D7${a.data.header.NX}` };
            return new SO.Volume.Data(volume, props);
        });
    },
    dispose({ b }) {
        b?.data.customProperties.dispose();
    }
});

type VolumeFromDsn6 = typeof VolumeFromDsn6
const VolumeFromDsn6 = PluginStateTransform.BuiltIn({
    name: 'volume-from-dsn6',
    display: { name: 'Volume from DSN6/BRIX', description: 'Create Volume from DSN6/BRIX data' },
    from: SO.Format.Dsn6,
    to: SO.Volume.Data,
    params(a) {
        return {
            voxelSize: PD.Vec3(Vec3.create(1, 1, 1)),
            entryId: PD.Text(''),
        };
    }
})({
    apply({ a, params }) {
        return Task.create('Create volume from DSN6/BRIX', async ctx => {
            const volume = await volumeFromDsn6(a.data, { ...params, label: a.data.name || a.label }).runInContext(ctx);
            const props = { label: volume.label || 'Volume', description: `Volume ${a.data.header.xExtent}\u00D7${a.data.header.yExtent}\u00D7${a.data.header.zExtent}` };
            return new SO.Volume.Data(volume, props);
        });
    },
    dispose({ b }) {
        b?.data.customProperties.dispose();
    }
});

type VolumeFromCube = typeof VolumeFromCube
const VolumeFromCube = PluginStateTransform.BuiltIn({
    name: 'volume-from-cube',
    display: { name: 'Volume from Cube', description: 'Create Volume from Cube data' },
    from: SO.Format.Cube,
    to: SO.Volume.Data,
    params(a) {
        const dataIndex = a ? PD.Select(0, a.data.header.dataSetIds.map((id, i) => [i, `${id}`] as const)) : PD.Numeric(0);
        return {
            dataIndex,
            entryId: PD.Text(''),
        };
    }
})({
    apply({ a, params }) {
        return Task.create('Create volume from Cube', async ctx => {
            const volume = await volumeFromCube(a.data, { ...params, label: a.data.name || a.label }).runInContext(ctx);
            const props = { label: volume.label || 'Volume', description: `Volume ${a.data.header.dim[0]}\u00D7${a.data.header.dim[1]}\u00D7${a.data.header.dim[2]}` };
            return new SO.Volume.Data(volume, props);
        });
    },
    dispose({ b }) {
        b?.data.customProperties.dispose();
    }
});

type VolumeFromDx = typeof VolumeFromDx
const VolumeFromDx = PluginStateTransform.BuiltIn({
    name: 'volume-from-dx',
    display: { name: 'Parse DX', description: 'Create volume from DX data.' },
    from: SO.Format.Dx,
    to: SO.Volume.Data
})({
    apply({ a }) {
        return Task.create('Parse DX', async ctx => {
            const volume = await volumeFromDx(a.data, { label: a.data.name || a.label }).runInContext(ctx);
            const props = { label: volume.label || 'Volume', description: `Volume ${a.data.header.dim[0]}\u00D7${a.data.header.dim[1]}\u00D7${a.data.header.dim[2]}` };
            return new SO.Volume.Data(volume, props);
        });
    },
    dispose({ b }) {
        b?.data.customProperties.dispose();
    }
});

type VolumeFromDensityServerCif = typeof VolumeFromDensityServerCif
const VolumeFromDensityServerCif = PluginStateTransform.BuiltIn({
    name: 'volume-from-density-server-cif',
    display: { name: 'Volume from density-server CIF', description: 'Identify and create all separate models in the specified CIF data block' },
    from: SO.Format.Cif,
    to: SO.Volume.Data,
    params(a) {
        if (!a) {
            return {
                blockHeader: PD.Optional(PD.Text(void 0, { description: 'Header of the block to parse. If none is specifed, the 1st data block in the file is used.' })),
                entryId: PD.Text(''),
            };
        }
        const blocks = a.data.blocks.slice(1); // zero block contains query meta-data
        return {
            blockHeader: PD.Optional(PD.Select(blocks[0] && blocks[0].header, blocks.map(b => [b.header, b.header] as [string, string]), { description: 'Header of the block to parse' })),
            entryId: PD.Text(''),
        };
    }
})({
    isApplicable: a => a.data.blocks.length > 0,
    apply({ a, params }) {
        return Task.create('Parse density-server CIF', async ctx => {
            const header = params.blockHeader || a.data.blocks[1].header; // zero block contains query meta-data
            const block = a.data.blocks.find(b => b.header === header);
            if (!block) throw new Error(`Data block '${[header]}' not found.`);
            const densityServerCif = CIF.schema.densityServer(block);
            const volume = await volumeFromDensityServerData(densityServerCif, { entryId: params.entryId }).runInContext(ctx);
            const [x, y, z] = volume.grid.cells.space.dimensions;
            const props = { label: params.entryId ?? densityServerCif.volume_data_3d_info.name.value(0), description: `Volume ${x}\u00D7${y}\u00D7${z}` };
            return new SO.Volume.Data(volume, props);
        });
    },
    dispose({ b }) {
        b?.data.customProperties.dispose();
    }
});

type VolumeFromSegmentationCif = typeof VolumeFromSegmentationCif
const VolumeFromSegmentationCif = PluginStateTransform.BuiltIn({
    name: 'volume-from-segmentation-cif',
    display: { name: 'Volume from Segmentation CIF' },
    from: SO.Format.Cif,
    to: SO.Volume.Data,
    params(a) {
        const blocks = a?.data.blocks.slice(1);
        const blockHeaderParam = blocks ?
            PD.Optional(PD.Select(blocks[0] && blocks[0].header, blocks.map(b => [b.header, b.header] as [string, string]), { description: 'Header of the block to parse' }))
            : PD.Optional(PD.Text(void 0, { description: 'Header of the block to parse. If none is specifed, the 1st data block in the file is used.' }));
        return {
            blockHeader: blockHeaderParam,
            segmentLabels: PD.ObjectList({ id: PD.Numeric(-1), label: PD.Text('') }, s => `${s.id} = ${s.label}`, { description: 'Mapping of segment IDs to segment labels' }),
            ownerId: PD.Text('', { isHidden: true, description: 'Reference to the object which manages this volume' }),
        };
    }
})({
    isApplicable: a => a.data.blocks.length > 0,
    apply({ a, params }) {
        return Task.create('Parse segmentation CIF', async ctx => {
            const header = params.blockHeader || a.data.blocks[1].header; // zero block contains query meta-data
            const block = a.data.blocks.find(b => b.header === header);
            if (!block) throw new Error(`Data block '${[header]}' not found.`);
            const segmentationCif = CIF.schema.segmentation(block);
            const segmentLabels: { [id: number]: string } = {};
            for (const segment of params.segmentLabels) segmentLabels[segment.id] = segment.label;
            const volume = await volumeFromSegmentationData(segmentationCif, { segmentLabels, ownerId: params.ownerId }).runInContext(ctx);
            const [x, y, z] = volume.grid.cells.space.dimensions;
            const props = { label: segmentationCif.volume_data_3d_info.name.value(0), description: `Segmentation ${x}\u00D7${y}\u00D7${z}` };
            return new SO.Volume.Data(volume, props);
        });
    },
    dispose({ b }) {
        b?.data.customProperties.dispose();
    }
});

type VolumeFromStructureFactorsCif = typeof VolumeFromStructureFactorsCif
const VolumeFromStructureFactorsCif = PluginStateTransform.BuiltIn({
    name: 'volume-from-structure-factors-cif',
    display: { name: 'Volume from Structure Factors CIF', description: 'Compute electron density map from structure factor reflection data (pdbx_FWT/pdbx_PHWT or pdbx_DELFWT/pdbx_DELPHWT)' },
    from: SO.Format.Cif,
    to: SO.Volume.Data,
    params(a) {
        const blockOptions: [string, string][] = a
            ? a.data.blocks.map(b => [b.header, b.header] as [string, string])
            : [];
        return {
            blockHeader: PD.Optional(blockOptions.length > 0
                ? PD.Select(blockOptions[0][0], blockOptions, { description: 'Header of the data block to use' })
                : PD.Text(void 0, { description: 'Header of the block to parse. Defaults to first block.' })),
            entryId: PD.Text(''),
            mapType: PD.Select<StructureFactorMapType>('2fo-fc', [['2fo-fc', '2Fo-Fc (pdbx_FWT/PHWT)'], ['fo-fc', 'Fo-Fc (pdbx_DELFWT/DELPHWT)']]),
        };
    }
})({
    isApplicable: a => a.data.blocks.length > 0,
    apply({ a, params }) {
        return Task.create('Parse structure factors CIF', async ctx => {
            const header = params.blockHeader || a.data.blocks[0].header;
            const block = a.data.blocks.find(b => b.header === header);
            if (!block) throw new Error(`Data block '${header}' not found.`);
            const sfCif = CIF.schema.SF(block);
            const volume = await volumeFromStructureFactors(sfCif, {
                entryId: params.entryId,
                mapType: params.mapType,
            }).runInContext(ctx);
            const [x, y, z] = volume.grid.cells.space.dimensions;
            const label = params.entryId || header;
            const mapLabel = params.mapType === 'fo-fc' ? 'Fo-Fc' : '2Fo-Fc';
            const props = { label, description: `${mapLabel} Volume ${x}\u00D7${y}\u00D7${z}` };
            return new SO.Volume.Data(volume, props);
        });
    },
    dispose({ b }) {
        b?.data.customProperties.dispose();
    }
});

type VolumeFromMtz = typeof VolumeFromMtz
const VolumeFromMtz = PluginStateTransform.BuiltIn({
    name: 'volume-from-mtz',
    display: { name: 'Volume from MTZ', description: 'Compute electron density map from MTZ reflection data' },
    from: SO.Format.Mtz,
    to: SO.Volume.Data,
    params(a) {
        if (!a) {
            return {
                ampLabel: PD.Text('FWT', { description: 'Column label of the structure-factor amplitude (type F)' }),
                phiLabel: PD.Text('PHWT', { description: 'Column label of the phase in degrees (type P)' }),
                entryId: PD.Text(''),
            };
        }
        const fCols = a.data.header.columns
            .filter(c => c.type === 'F')
            .map(c => [c.label, c.label] as [string, string]);
        const pCols = a.data.header.columns
            .filter(c => c.type === 'P')
            .map(c => [c.label, c.label] as [string, string]);
        return {
            ampLabel: fCols.length > 0
                ? PD.Select(fCols[0][0], fCols, { description: 'Structure-factor amplitude column (type F)' })
                : PD.Text('FWT', { description: 'Structure-factor amplitude column label (type F)' }),
            phiLabel: pCols.length > 0
                ? PD.Select(pCols[0][0], pCols, { description: 'Phase column in degrees (type P)' })
                : PD.Text('PHWT', { description: 'Phase column label in degrees (type P)' }),
            entryId: PD.Text(''),
        };
    }
})({
    apply({ a, params }) {
        return Task.create('Compute volume from MTZ', async ctx => {
            const volume = await volumeFromMtz(a.data, {
                ampLabel: params.ampLabel,
                phiLabel: params.phiLabel,
                entryId: params.entryId || undefined,
                label: params.entryId || a.data.name || undefined,
            }).runInContext(ctx);
            const [x, y, z] = volume.grid.cells.space.dimensions;
            const label = params.entryId || a.data.name || 'Volume';
            const props = { label, description: `Volume ${x}\u00D7${y}\u00D7${z}` };
            return new SO.Volume.Data(volume, props);
        });
    },
    dispose({ b }) {
        b?.data.customProperties.dispose();
    }
});

type AssignColorVolume = typeof AssignColorVolume
const AssignColorVolume = PluginStateTransform.BuiltIn({
    name: 'assign-color-volume',
    display: { name: 'Assign Color Volume', description: 'Assigns another volume to be available for coloring.' },
    from: SO.Volume.Data,
    to: SO.Volume.Data,
    isDecorator: true,
    params(a, plugin: PluginContext) {
        if (!a) return { ref: PD.Text() };
        const cells = plugin.state.data.select(StateSelection.Generators.root.subtree().ofType(SO.Volume.Data).filter(cell => !!cell.obj && !cell.obj?.data.colorVolume && cell.obj !== a));
        if (cells.length === 0) return { ref: PD.Text('', { isHidden: true }) };
        return { ref: PD.Select(cells[0].transform.ref, cells.map(c => [c.transform.ref, c.obj!.label])) };
    }
})({
    apply({ a, params, dependencies }) {
        return Task.create('Assign Color Volume', async ctx => {
            if (!dependencies || !dependencies[params.ref]) {
                throw new Error('Dependency not available.');
            }
            const colorVolume = dependencies[params.ref].data as Volume;
            const volume: Volume = {
                ...a.data,
                colorVolume,
                _localPropertyData: Object.create(null),
            };
            const props = { label: a.label, description: 'Volume + Colors' };
            return new SO.Volume.Data(volume, props);
        });
    },
    getDependencies: ({ ref }) => ref ? [ref as StateTransform.Ref] : []
});

type VolumeTransform = typeof VolumeTransform;
const VolumeTransform = PluginStateTransform.BuiltIn({
    name: 'volume-transform',
    display: { name: 'Transform Volume' },
    isDecorator: true,
    from: SO.Volume.Data,
    to: SO.Volume.Data,
    params: {
        transform: TransformParam,
    },
})({
    canAutoUpdate() {
        return false;
    },
    apply({ a, params }) {
        // similar to StateTransforms.Model.TransformStructureConformation;
        const center = transformParamsNeedCentroid(params.transform) ? Grid.getBoundingSphere(a.data.grid).center : Vec3.unit;
        const transform = getTransformFromParams(params.transform, center);
        const gridTransform = {
            kind: 'matrix' as const,
            matrix: Mat4.mul(Mat4(), transform, Grid.getGridToCartesianTransform(a.data.grid)),
        };
        return new SO.Volume.Data({
            ...a.data,
            grid: {
                ...a.data.grid,
                transform: gridTransform,
            },
            _localPropertyData: Object.create(null),
        }, {
            label: a.label,
            description: `${a.description} [Transformed]`,
        });
    },
});

type VolumeInstances = typeof VolumeInstances;
const VolumeInstances = PluginStateTransform.BuiltIn({
    name: 'volume-instances',
    display: { name: 'Volume Instances' },
    isDecorator: true,
    from: SO.Volume.Data,
    to: SO.Volume.Data,
    params: {
        mode: PD.Select('transforms', PD.arrayToOptions(['transforms', 'periodicRange'])),
        transforms: PD.ObjectList({ transform: TransformParam }, () => 'Transform', { hideIf: c => c.mode !== 'transforms' }),
        periodicRange: PD.Group({
            min: PD.Vec3(Vec3.create(0, 0, 0), { min: -10, max: 10, step: 1 }, { label: 'Min', description: 'Inclusive lower bound of the translation range (x, y, z)' }),
            max: PD.Vec3(Vec3.create(1, 1, 1), { min: -10, max: 10, step: 1 }, { label: 'Max', description: 'Exclusive upper bound of the translation range (x, y, z)' }),
        }, { hideIf: c => c.mode !== 'periodicRange', isFlat: true }),
    },
})({
    canAutoUpdate() {
        return true;
    },
    apply({ a, params }) {
        let instances: { transform: Mat4 }[] = [];
        if (params.mode === 'transforms') {
            const center = params.transforms.some(t => transformParamsNeedCentroid(t.transform)) ? Grid.getBoundingSphere(a.data.grid).center : Vec3.unit;
            instances = params.transforms.map(t => ({ transform: getTransformFromParams(t.transform, center) }));
        } else if (params.mode === 'periodicRange' && Volume.isPeriodic(a.data)) {
            const dims = a.data.grid.cells.space.dimensions;
            const gridToCartn = Grid.getGridToCartesianTransform(a.data.grid);
            const { min, max } = params.periodicRange;

            const [minA, minB, minC] = min;
            const [maxA, maxB, maxC] = max;

            const t = Vec3();
            for (let ia = minA; ia < maxA; ia++) {
                for (let ib = minB; ib < maxB; ib++) {
                    for (let ic = minC; ic < maxC; ic++) {
                        Vec3.set(t, dims[0] * ia, dims[1] * ib, dims[2] * ic);
                        Vec3.transformMat4(t, t, gridToCartn);
                        instances.push({ transform: Mat4.fromTranslation(Mat4(), t) });
                    }
                }
            }
        }

        if (!instances.length) {
            return a;
        }
        return new SO.Volume.Data({
            ...a.data,
            instances,
            _localPropertyData: Object.create(null),
        }, {
            label: a.label,
            description: `${a.description} [Instanced]`,
        });
    },
});

type CustomVolumeProperties = typeof CustomVolumeProperties
const CustomVolumeProperties = PluginStateTransform.BuiltIn({
    name: 'custom-volume-properties',
    display: { name: 'Custom Volume Properties' },
    isDecorator: true,
    from: SO.Volume.Data,
    to: SO.Volume.Data,
    params: (a, ctx: PluginContext) => {
        return ctx.customVolumeProperties.getParams(a?.data);
    }
})({
    apply({ a, params }, ctx: PluginContext) {
        return Task.create('Custom Volume Props', async taskCtx => {
            await attachVolumeProps(a.data, ctx, taskCtx, params);
            return new SO.Volume.Data(a.data, { label: a.label, description: a.description });
        });
    },
    update({ a, b, oldParams, newParams }, ctx: PluginContext) {
        return Task.create('Custom Volume Props', async taskCtx => {
            b.data = a.data;
            b.label = a.label;
            b.description = a.description;
            for (const name of oldParams.autoAttach) {
                const property = ctx.customVolumeProperties.get(name);
                if (!property) continue;
                a.data.customProperties.reference(property.descriptor, false);
            }
            await attachVolumeProps(a.data, ctx, taskCtx, newParams);
            return StateTransformer.UpdateResult.Updated;
        });
    },
    dispose({ b }) {
        b?.data.customProperties.dispose();
    }
});
async function attachVolumeProps(volume: Volume, ctx: PluginContext, taskCtx: RuntimeContext, params: ReturnType<CustomVolumeProperties['createDefaultParams']>) {
    const propertyCtx = { runtime: taskCtx, assetManager: ctx.managers.asset, errorContext: ctx.errorContext };
    const { autoAttach, properties } = params;
    for (const name of Object.keys(properties)) {
        const property = ctx.customVolumeProperties.get(name)!;
        const props = properties[name];
        if (autoAttach.includes(name) || property.isHidden) {
            try {
                await property.attach(propertyCtx, volume, props, true);
            } catch (e) {
                ctx.log.warn(`Error attaching volume prop '${name}': ${e}`);
            }
        } else {
            property.set(volume, props);
        }
    }
}
